<template>
  <div class="hermes-tabulator">
    <div v-if="loading" class="hermes-tabulator__loading">Loading…</div>
    <div v-else-if="error" class="hermes-tabulator__error alert alert-warning">{{ error }}</div>
    <div v-else-if="!rows.length" class="hermes-tabulator__empty">No rows.</div>
    <div v-show="!loading && !error && rows.length" ref="tableEl" class="hermes-tabulator__table" />
  </div>
</template>

<script>
/**
 * HermesTabulator — thin Vue wrapper around tabulator-tables v6.3.1.
 *
 * Owns: instantiating the Tabulator engine, mapping `props.columns` to
 * Tabulator's column definitions, header filters + sort + page size +
 * column-visibility persistence to localStorage (keyed by `:storageKey`).
 *
 * Does NOT own: data loading. Callers pass `:rows` (already-fetched JSON
 * array). Lets the host component keep using its own fetch layer (useApi
 * etc.) and just feed rows in.
 *
 * Future migration: when `@apexl/icarus-tabulator` extracts from TC,
 * swap this for `<TabulatorEntity>`-style usage with workspace-aware
 * list-config from io-page-layouts 1.1+ (`GET /api/v1/page-layouts/list-config`).
 * For now, this gets Hermes proper tables with zero TC-local deps.
 */
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  EditModule, FilterModule, FormatModule, InteractionModule,
  PageModule, ResizeColumnsModule, SortModule, Tabulator,
} from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator.min.css';

Tabulator.registerModule([
  EditModule, FilterModule, FormatModule, InteractionModule,
  PageModule, ResizeColumnsModule, SortModule,
]);

export default {
  name: 'HermesTabulator',
  props: {
    /** Array of row objects. */
    rows:        { type: Array,  default: () => [] },
    /**
     * Column defs, lean Hermes shape:
     *   { key, label, format?, sorter?, width?, hozAlign?, headerFilter? }
     * `format(value, row)` returns a string (HTML-safe — wrapper escapes).
     */
    columns:     { type: Array,  required: true },
    /** localStorage key for column visibility / order persistence. */
    storageKey:  { type: String, default: '' },
    /** Initial sort: { column, dir }. */
    initialSort: { type: Object, default: () => ({}) },
    /** Page size; pass 0 to disable pagination. */
    pageSize:    { type: Number, default: 25 },
    loading:     { type: Boolean, default: false },
    error:       { type: String,  default: '' },
  },
  emits: ['row-click'],
  setup(props, { emit }) {
    const tableEl = ref(null);
    let tabulator = null;

    function buildColumnDef(c) {
      const def = {
        title: c.label,
        field: c.key,
        headerFilter: c.headerFilter ?? 'input',
        headerSort: c.sorter !== false,
        resizable: true,
      };
      if (c.width)    def.width = c.width;
      if (c.hozAlign) def.hozAlign = c.hozAlign;
      if (c.sorter)   def.sorter = c.sorter;       // string (e.g. 'number') or function
      // Per-cell click handler (action columns). Stops the event so the row-click
      // handler below doesn't also fire (e.g. a "Refund" button in its own column).
      if (typeof c.cellClick === 'function') {
        def.headerSort = false;
        def.cellClick = (e, cell) => {
          e.stopPropagation();
          c.cellClick(cell.getRow().getData(), e);
        };
      }
      if (c.format) {
        def.formatter = (cell) => {
          const row = cell.getRow().getData();
          const raw = cell.getValue();
          return String(c.format(raw, row));
        };
      } else {
        // Default: escape + dash on null/undefined.
        def.formatter = (cell) => {
          const v = cell.getValue();
          if (v === null || v === undefined || v === '') return '<span class="text-muted">—</span>';
          return String(v).replace(/[<>&"]/g, ch => ({ '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;' }[ch]));
        };
      }
      return def;
    }

    function build() {
      if (!tableEl.value) return;
      if (tabulator) {
        try { tabulator.destroy(); } catch (_) {}
        tabulator = null;
      }
      const opts = {
        data: props.rows,
        columns: props.columns.map(buildColumnDef),
        // `fitColumns` works with the base module set we registered.
        // `fitDataStretch` + `responsiveLayout: 'collapse'` need
        // ResponsiveLayoutModule + FrozenColumnsModule; without them
        // they throw "Cannot read properties of undefined (reading
        // 'visible')" deep inside Layout.fitDataStretch.
        layout: 'fitColumns',
        height: 'auto',
        movableColumns: true,
        placeholder: 'No rows.',
      };
      if (props.pageSize > 0) {
        opts.pagination = true;
        opts.paginationSize = props.pageSize;
        opts.paginationSizeSelector = [10, 25, 50, 100];
      }
      if (props.initialSort?.column) {
        opts.initialSort = [{ column: props.initialSort.column, dir: props.initialSort.dir || 'desc' }];
      }
      if (props.storageKey) {
        opts.persistence = { sort: true, filter: true, columns: true, page: true };
        opts.persistenceID = `hermes-tab:${props.storageKey}`;
      }
      tabulator = new Tabulator(tableEl.value, opts);

      // Emit the clicked row's data so hosts can navigate (e.g. row → detail).
      // Since Tabulator 5 this is an EVENT bound with .on(); a `rowClick` entry in
      // the options is ignored, which left every row in every list unclickable.
      tabulator.on('rowClick', (_e, row) => emit('row-click', row.getData()));
    }

    onMounted(build);
    onBeforeUnmount(() => {
      if (tabulator) { try { tabulator.destroy(); } catch (_) {} tabulator = null; }
    });

    // Rebuild on rows / columns / storageKey change. Cheap for our 100-row admin lists.
    watch(() => [props.rows, props.columns, props.storageKey], build, { deep: false });

    return { tableEl };
  },
};
</script>

<style>
/* Tabulator's bundled theme is white-on-white-ish; nudge for the Hermes admin shell. */
.hermes-tabulator { font-family: var(--h-font-body, system-ui); font-size: 0.85rem; }
.hermes-tabulator__loading,
.hermes-tabulator__empty { color: var(--h-text-tertiary, #94A3B8); padding: 24px; text-align: center; }
/* Every colour is a token: Tabulator's own theme paints rows white and the header
   grey, which in the dark theme left light text on a white row. */
.hermes-tabulator .tabulator { border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius, 6px); background: var(--h-bg-card, #fff); }
.hermes-tabulator .tabulator .tabulator-header { background: var(--h-bg-subtle, #F8FAFC); border-bottom: 1px solid var(--h-border, #E2E8F0); }
.hermes-tabulator .tabulator .tabulator-header .tabulator-col {
  background: transparent;
  border-right: 1px solid var(--h-border, #E2E8F0);
  color: var(--h-text-secondary, #64748B);
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.hermes-tabulator .tabulator .tabulator-header .tabulator-col .tabulator-col-content { padding: 10px 12px; }
.hermes-tabulator .tabulator .tabulator-header .tabulator-col .tabulator-header-filter input {
  border: 1px solid var(--h-border, #E2E8F0);
  border-radius: var(--h-radius, 6px);
  padding: 4px 8px;
  font-size: 0.8rem;
  background: var(--h-bg-card, #fff);
  color: var(--h-text-primary, #0F172A);
}
.hermes-tabulator .tabulator .tabulator-row { background: var(--h-bg-card, #fff); border-bottom: 1px solid var(--h-border, #E2E8F0); }
.hermes-tabulator .tabulator .tabulator-row.tabulator-row-even { background: var(--h-bg-card, #fff); }
.hermes-tabulator .tabulator .tabulator-row:hover,
.hermes-tabulator .tabulator .tabulator-row.tabulator-row-even:hover { background: var(--h-bg-subtle, #F8FAFC); }
.hermes-tabulator .tabulator .tabulator-row .tabulator-cell { padding: 10px 12px; border-right: 1px solid var(--h-border, #E2E8F0); color: var(--h-text-primary, #0F172A); }
.hermes-tabulator .tabulator .tabulator-footer { background: var(--h-bg-subtle, #F8FAFC); border-top: 1px solid var(--h-border, #E2E8F0); padding: 4px 8px; color: var(--h-text-secondary, #64748B); }
.hermes-tabulator .tabulator .tabulator-footer .tabulator-page,
.hermes-tabulator .tabulator .tabulator-footer .tabulator-page-size { background: var(--h-bg-card, #fff); color: var(--h-text-primary, #0F172A); border-color: var(--h-border, #E2E8F0); }
.hermes-tabulator .tabulator .tabulator-paginator label,
.hermes-tabulator .tabulator .tabulator-paginator select,
.hermes-tabulator .tabulator .tabulator-page { font-size: 0.78rem; }
.hermes-tabulator .text-muted { color: var(--h-text-tertiary, #94A3B8); }
</style>
